/**
 * Model representing a policy received from the server.
 */

import { AiService } from "../agentStep.model";
import { Platform, ResourceType, UserActionTimestamps } from "../common";

export enum RuleCategory {
    data_flow = "dataFlow",
    least_privilege = "leastPrivilege",
    identity_management = "identityManagement",
    custom_code = "customCode",
    security_monitoring = "securityMonitoring",
    authentication = "authentication",
    encryption = "encryption",
    environment_separation = "environmentSeparation",
    hygiene = "hygiene",
    third_party_dependency = "thirdPartyDependency",
}

export enum RuleTag {
    preview = "preview",
    generalAvailability = "generalAvailability",
    deprecated = "deprecated",
    exploitable = "exploitable",
    alert = "alert",
    AIDR = "AIDR",
    SPM = "SPM",
}

/** * Cvss base
 *
 * Cvss - common vulnerability scoring system: open industry standard for assessing the severity of vulnerabilities
 * score consists of base score and temporal.
 */

export enum CvssBase {
    base_2 = "2",
    base_3 = "3",
}

/**
 * a class for cvss scoring
 * Cvss - common vulnerability scoring system: open industry standard for assessing the severity of vulnerabilities
 */
export interface Cvss {
    score: string;
    vector: string;
    base: CvssBase;
}

/**
 * An enum representing vulnerability severity
 */
export enum Severity {
    high = "high",
    medium = "medium",
    low = "low",
    informational = "informational",
}

/**
 * An enum representing security risks
 */
export enum Risk {
    potentialCredentialsCompromise = "potentialCredentialsCompromise",
    potentialDataCompromise = "potentialDataCompromise",
    potentialMitm = "potentialMitm",
    potentialExploit = "potentialExploit",
    potentialMalware = "potentialMalware",
    potentialDataLeakage = "potentialDataLeakage",
    potentialDataLoss = "potentialDataLoss",
    potentialHarmfulData = "potentialHarmfulData",
    potentialInvalidData = "potentialInvalidData",
    potentialCredentialsOversharing = "potentialCredentialsOversharing",
    potentialAccountTakeover = "potentialAccountTakeover",
    potentialAdAuthenticationBreach = "potentialAdAuthenticationBreach",
    potentialLateralMovement = "potentialLateralMovement",
    attackSurfaceExpansion = "attackSurfaceExpansion",
    businessContinuity = "businessContinuity",
    businessDataExposureToOutsiders = "businessDataExposureToOutsiders",
    businessDataExposureToEavesdroppers = "businessDataExposureToEavesdroppers",
    businessDataCanBeFullyControlledByOutsiders = "businessDataCanBeFullyControlledByOutsiders",
    possibleRougeActivity = "possibleRougeActivity",
    organizationVisibility = "organizationVisibility",
    costOptimization = "costOptimization",
    mitmAttacks = "mitmAttacks",
    maliciousActivityIndicator = "maliciousActivityIndicator",
    potentialDdosAttacks = "potentialDdosAttacks",
    insufficientSecurityData = "insufficientSecurityData",
}

export enum RuleStatus {
    on = "on",
    off = "off",
    unavailable = "unavailable",
}

export interface BaseViolationRule {
    id: string;
    displayName: string;
    description: string;
    severity: Severity;
    category: RuleCategory;
    cvssScores: Cvss[];
    platform: Platform | AiService;
    summary: string;
    riskReason: string;
    triageSuggestion: string;
    fixSuggestion: string;
    riskLabels: Risk[];
    resourceType: ResourceType;
    owasp: string[];
    attack: string[];
    status: RuleStatus;
    tags: RuleTag[];
    effectivePolicySettings: string[];
}

export interface ViolationRule extends BaseViolationRule {
    integrationId: string;
    policyId: string;
    userActionTimeStamps?: UserActionTimestamps;
    isAvailable?: boolean;
}

export class ViolationRuleUpdateApiModel {
    public status?: RuleStatus;
    public severity?: Severity;
}
